import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PlusIcon, Trash2, Loader2, RotateCcw } from 'lucide-react';
import {
  UsersWrapper,
  UsersContainer,
  PageHeader,
  HeaderLeft,
  Title,
  Description,
  AddUserButton,
  TableCard,
  TableToolbar,
  SearchSlot,
  UserNameRow,
  UserName,
  EmailText,
  JoinedText,
  RowActionsGroup,
  RowActionButton,
  HeaderActionsRow,
} from './users.styles';

import SearchBar from '../AdminTickets/components/SearchBar/SearchBar';
import { Table } from '../../../components/uielements/table';
import {
  OutlineButon,
  EmptyState,
  RetryButton,
  SpinWrapper,
} from '../../../settings/appStyle/global.style';
import { useAdminUsersQuery, useDeleteUser } from './hooks/useUsersAdmin';
import CreateUserModal from './modals/CreateUserModal';

const PAGE_SIZE = 10;

const formatDate = (isoString) => (isoString ? new Date(isoString).toLocaleDateString() : '—');
const isRootAccount = (user) => Number(user?.rootUser) === 1;

const Users = () => {
  const navigate = useNavigate();
  const { clientId } = useParams();

  const [searchValue, setSearchValue] = useState('');
  const [page, setPage] = useState(1);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState(null);

  const { data, isLoading, isError, refetch, isFetching } = useAdminUsersQuery(clientId, {
    page: page,
    pageSize: PAGE_SIZE,
  });

  const deleteUserMutation = useDeleteUser({
    onSuccess: () => setDeletingUserId(null),
    onError: (err) => {
      setDeletingUserId(null);
      window.alert(`Failed to remove user${err?.message ? `: ${err.message}` : '.'}`);
    },
  });

  const users = data?.users ?? [];
  const total = data?.totalUsers ?? 0;

  const filteredUsers = useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    if (!query) return users;
    return users.filter(
      (user) =>
        (user.username || '').toLowerCase().includes(query) ||
        (user.email || '').toLowerCase().includes(query)
    );
  }, [users, searchValue]);

  const handleSearchChange = (value) => {
    setSearchValue(value);
    setPage(0);
  };

  const handleBack = () => navigate('/clients');

  const handleAddUser = () => setIsCreateModalOpen(true);
  const handleCloseCreateModal = () => setIsCreateModalOpen(false);

  const handleDeleteUser = (user) => {
    if (isRootAccount(user)) return;

    const confirmed = window.confirm(`Remove ${user.username} from this client?`);
    if (!confirmed) return;

    setDeletingUserId(user._id);
    deleteUserMutation.mutate({ userId: user._id, clientId });
  };

  const columns = useMemo(
    () => [
      {
        Header: 'Name',
        accessor: 'username',
        width: '30%',
        Cell: ({ value }) => (
          <UserNameRow>
            <UserName>{value}</UserName>
          </UserNameRow>
        ),
      },
      {
        Header: 'Email',
        accessor: 'email',
        width: '32%',
        Cell: ({ value }) => <EmailText>{value}</EmailText>,
      },
      {
        Header: 'Joined',
        accessor: 'createdAt',
        width: '18%',
        Cell: ({ value }) => <JoinedText>{formatDate(value)}</JoinedText>,
      },
      {
        Header: 'Actions',
        accessor: '_id',
        width: '18%',
        disableSortBy: true,
        Cell: ({ row }) => {
          const user = row.original;
          const isDeletingThis = deletingUserId === user._id;
          const isRoot = isRootAccount(user);
          const isDeleteDisabled = isRoot;
          const deleteTitle = isRoot ? 'Root account cannot be removed' : `Remove ${user.username}`;

          return (
            <RowActionsGroup>
              <RowActionButton
                type="button"
                $danger
                title={deleteTitle}
                aria-label={deleteTitle}
                onClick={() => !isDeleteDisabled && handleDeleteUser(user)}
                disabled={isDeletingThis || isDeleteDisabled}
                style={isDeleteDisabled ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
              >
                {isDeletingThis ? <Loader2 data-spin="true" /> : <Trash2 />}
              </RowActionButton>
            </RowActionsGroup>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deletingUserId]
  );

  if (!clientId) {
    return (
      <UsersWrapper>
        <UsersContainer>
          <EmptyState>
            <p>No client selected</p>
            <p>Open this page from a specific client to manage its users</p>
            <OutlineButon type="button" onClick={() => navigate('/clients')}>
              Back to clients
            </OutlineButon>
          </EmptyState>
        </UsersContainer>
      </UsersWrapper>
    );
  }

  return (
    <UsersWrapper>
      <UsersContainer>
        <PageHeader>
          <HeaderLeft>
            <Title>Users</Title>
            <Description>People with access to this client's support workspace.</Description>
          </HeaderLeft>
          <HeaderActionsRow>
            <OutlineButon onClick={handleBack} type="button">
              Back
            </OutlineButon>
            <AddUserButton type="button" onClick={handleAddUser}>
              <PlusIcon size={16} />
              Add User
            </AddUserButton>
          </HeaderActionsRow>
        </PageHeader>

        <TableCard>
          <TableToolbar>
            <SearchSlot>
              <SearchBar
                value={searchValue}
                onChange={handleSearchChange}
                placeholder="Search users by name or email…"
              />
            </SearchSlot>
          </TableToolbar>

          {isLoading ? (
            <>
              <EmptyState>
                <SpinWrapper>
                  <Loader2 />
                </SpinWrapper>
                Loading user listing...
              </EmptyState>
            </>
          ) : isError ? (
            <EmptyState>
              Failed to load users. Please try again later
              <RetryButton type="button" onClick={() => refetch()} disabled={isFetching}>
                <RotateCcw />
                {isFetching ? 'Retrying…' : 'Retry'}
              </RetryButton>
            </EmptyState>
          ) : (
            <Table
              data={filteredUsers}
              columns={columns}
              hasFooter
              page={page - 1}
              pageSize={PAGE_SIZE}
              total={total}
              isLoading={isLoading}
              isFetching={isFetching}
              prevPage={() => setPage((p) => Math.max(p - 1, 1))}
              nextPage={() => setPage((p) => p + 1)}
              emptyStateText="No users match your search"
            />
          )}
        </TableCard>
      </UsersContainer>

      <CreateUserModal
        isOpen={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        clientId={clientId}
      />
    </UsersWrapper>
  );
};

export default Users;
